import type { StorageQuota } from './types.js';

/** Warning threshold — see docs/MEDIA_LIBRARY_ARCHITECTURE.md §4.3 */
export const QUOTA_WARNING_RATIO = 0.9;

export type QuotaLevel = 'ok' | 'warning' | 'full';

export type MediaLibraryStorageMode =
  | 'electron-fs'
  | 'remote-server'
  | 'browser-limited-fallback';

export function resolveMediaLibraryStorageMode(quota: StorageQuota): MediaLibraryStorageMode {
  if (quota.backend === 'electron-fs') return 'electron-fs';
  if (quota.backend === 'server') return 'remote-server';
  return 'browser-limited-fallback';
}

export function getQuotaLevel(quota: StorageQuota): QuotaLevel {
  if (quota.limitBytes <= 0) return 'ok';
  if (quota.usedBytes >= quota.limitBytes) return 'full';
  if (quota.usedBytes >= quota.limitBytes * QUOTA_WARNING_RATIO) return 'warning';
  return 'ok';
}

export function isQuotaFull(quota: StorageQuota): boolean {
  return getQuotaLevel(quota) === 'full';
}

export function isQuotaWarning(quota: StorageQuota): boolean {
  const level = getQuotaLevel(quota);
  return level === 'warning' || level === 'full';
}

/** Bytes/limit for `__buffer__` UI and recorder gating. */
export function resolveBufferQuota(quota: StorageQuota): { usedBytes: number; limitBytes: number } {
  return {
    usedBytes: quota.bufferUsedBytes ?? quota.usedBytes,
    limitBytes: quota.bufferLimitBytes ?? quota.limitBytes,
  };
}

export function getBufferQuotaLevel(quota: StorageQuota): QuotaLevel {
  const { usedBytes, limitBytes } = resolveBufferQuota(quota);
  if (limitBytes <= 0) return 'ok';
  if (usedBytes >= limitBytes) return 'full';
  if (usedBytes >= limitBytes * QUOTA_WARNING_RATIO) return 'warning';
  return 'ok';
}

export function isBufferQuotaFull(quota: StorageQuota): boolean {
  return getBufferQuotaLevel(quota) === 'full';
}

/** Whether `__buffer__` applies a max sample count (browser-limited fallback only). */
export function isBufferSampleCountCapActive(quota: StorageQuota): boolean {
  return resolveMediaLibraryStorageMode(quota) === 'browser-limited-fallback';
}

export function isBufferRecordingBlocked(
  quota: StorageQuota,
  sampleCount: number,
  maxBufferSamples: number,
): boolean {
  if (isBufferQuotaFull(quota)) return true;
  if (!isBufferSampleCountCapActive(quota)) return false;
  return sampleCount >= maxBufferSamples;
}
